import { useState, useEffect, useCallback } from 'react';
import learnApiClient from '../api/learnApiClient';

export interface CalendarEntry {
  id: number;
  source_type: 'coaching_session' | 'tms_event' | 'tms_session' | 'ilt_session';
  title: string;
  start_at: string;
  end_at: string;
  coach_name: string | null;
  coachee_name: string | null;
  status: 'scheduled' | 'ongoing' | 'completed' | 'cancelled';
  program_name: string | null;
  engagement_code: string | null;
}

interface UseCalendarEntriesResult {
  entries: CalendarEntry[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useCalendarEntries = (year: number, month: number): UseCalendarEntriesResult => {
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const start = new Date(year, month, 1).toISOString();
      const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString();

      const response = await learnApiClient.get('/calendar/entries', {
        params: { start, end, limit: 500, offset: 0 },
      });

      const raw = response.data;
      const data: CalendarEntry[] = Array.isArray(raw) ? raw : raw?.data || [];
      setEntries(data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Session expired');
      } else if (err.response?.status === 403) {
        setError('No permission');
      } else {
        setError('Failed to load calendar');
      }
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return { entries, loading, error, refetch: fetchEntries };
};
